import { forwardRef, useRef, type InputHTMLAttributes } from 'react';
import clsx from 'clsx';

import { CloseIcon, SearchIcon } from '../internal/icons';
import { useMergedFieldAria } from './FormField';
import { VisuallyHidden } from './VisuallyHidden';
import styles from './SearchInput.module.css';
import type { FieldSize } from './TextInput';

export interface SearchInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  size?: FieldSize;
  /** Accessible name when no visible label exists (a toolbar filter). */
  label?: string;
  /** Called on the clear button and on Escape. */
  onClear?: () => void;
  clearLabel?: string;
}

/**
 * `type="search"` so the platform's clear affordance and mobile keyboard apply.
 * The input keeps its own border and its own focus ring; the clear button is a
 * real button with its own name and its own ring, which is why this is not a
 * composite `data-focus-scope="field"` wrapper.
 */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  { size = 'md', label, onClear, clearLabel = 'Clear search', className, ...rest },
  forwardedRef,
) {
  const innerRef = useRef<HTMLInputElement | null>(null);
  const aria = useMergedFieldAria({
    id: rest.id,
    describedBy: rest['aria-describedby'],
    required: rest.required,
  });

  const hasValue = typeof rest.value === 'string' ? rest.value.length > 0 : undefined;

  return (
    <span className={clsx(styles.wrapper, styles[size], className)}>
      <SearchIcon className={styles.searchIcon} />
      <input
        {...rest}
        {...aria}
        ref={(node) => {
          innerRef.current = node;
          if (typeof forwardedRef === 'function') forwardedRef(node);
          else if (forwardedRef !== null) forwardedRef.current = node;
        }}
        type="search"
        role="searchbox"
        aria-label={label ?? rest['aria-label']}
        className={styles.input}
        onKeyDown={(event) => {
          rest.onKeyDown?.(event);
          if (event.key === 'Escape' && onClear !== undefined) {
            event.stopPropagation();
            onClear();
          }
        }}
      />
      {onClear !== undefined && hasValue !== false && (
        <button type="button" className={styles.clear} onClick={onClear}>
          <CloseIcon />
          <VisuallyHidden>{clearLabel}</VisuallyHidden>
        </button>
      )}
    </span>
  );
});
